import React, { useEffect, useMemo, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Pagination from "@material-ui/lab/Pagination";
import HighlightOffOutlinedIcon from "@material-ui/icons/HighlightOffOutlined";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch } from "react-redux";
import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import AddVehicleDialog from "../AddVehicleNew";
import { setSnackbar } from "../../js/reducers/snackbar";

const ROWS_OPTIONS = [5, 10, 20];

const COLORS = {
  navyDark: "#1e40af",
  blue800: "#1e40af",
  blue700: "#1d4ed8",
  blue100: "#dbeafe",
  blue50: "#eff6ff",
  emerald700: "#047857",
  emerald50: "#ecfdf5",
  emerald200: "#a7f3d0",
  slate500: "#64748b",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  amber500: "#f59e0b",
  red500: "#ef4444",
  border: "#e2e8f0",
  muted: "#f1f5f9",
  mutedForeground: "#64748b",
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    padding: "24px",
    backgroundColor: "#F8FAFC",
    minHeight: "calc(100vh - 127px)",
  },

  main: {
    // maxWidth: 1400,
    margin: "0 auto",
  },

  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },

  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },

  pageIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
  },

  addButton: {
    textTransform: "none",
    backgroundColor: COLORS.blue700,
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: COLORS.blue800,
    },
    "& .MuiButton-label": {
      fontSize: "13px",
    },
  },

  tableCard: {
    borderRadius: 16,
    overflow: "hidden",
  },

  toolbar: {
    padding: "0px 0px 8px 24px",
    borderBottom: `1px solid ${COLORS.border}`,
  },

  searchField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiOutlinedInput-root": {
      height: 50,
      width: 250,
      backgroundColor: COLORS.muted,
      borderRadius: 8,
    },
  },

  searchIcon: {
    fontSize: 18,
    color: COLORS.mutedForeground,
  },

  table: {
    tableLayout: "fixed",
  },

  tableHeadRow: {
    backgroundColor: "rgba(241,245,249,0.8)",
  },

  tableHeadCell: {
    fontSize: 12,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
  },

  makeChip: {
    backgroundColor: COLORS.blue50,
    color: COLORS.blue700,
    border: `1px solid ${COLORS.blue100}`,
    fontSize: "0.85rem",
  },

  normalRow: {
    backgroundColor: "#fff",
    "& .MuiTypography-root": {
      color: "#000",
      fontSize: "0.85rem",
    },
    "&:hover": {
      backgroundColor: "rgba(241,245,249,0.5)",
    },
  },

  emptyCell: {
    padding: "48px 0",
    color: COLORS.mutedForeground,
  },

  footer: {
    padding: "12px 24px",
    borderTop: `1px solid ${COLORS.border}`,
    backgroundColor: "rgba(241,245,249,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    display: "flex",
    alignItems: "center",
  },

  footerText: {
    color: COLORS.mutedForeground,
    marginRight: 8,
  },

  rowsSelect: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiSelect-select": {
      padding: "5px 25px 5px 10px",
    },
  },

  footerRight: {
    display: "flex",
    alignItems: "center",
  },

  pageText: {
    color: COLORS.mutedForeground,
    marginRight: 12,
  },

  /* ── Delete Dialog ── */
  dialogPaper: {
    borderRadius: "15px !important",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.05) !important",
  },
  dialogRoot: {
    zIndex: "1400 !important",
  },
  dialogHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#FFF5F5",
  },
  dialogHeaderIconBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#fee2e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogHeaderIcon: {
    fontSize: "22px !important",
    color: "#dc2626",
  },
  dialogHeaderTitle: {
    fontWeight: 700,
    fontSize: "16px !important",
    color: "#991b1b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    marginTop: 2,
  },
  dialogBody: {
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  dialogContentMain: {
    fontWeight: 500,
    fontSize: "14px !important",
    color: "#0f172a",
    lineHeight: 1.6,
  },
  dialogContentSub: {
    fontWeight: 400,
    fontSize: "12px !important",
    color: "#64748b",
    lineHeight: 1.5,
  },
  dialogFooter: {
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
  },
  dialogBtnOutlined: {
    borderRadius: 8,
    fontWeight: 600,
    color: "#0f172a",
    borderColor: "#e2e8f0",
    textTransform: "none",
    padding: "8px 20px",
    fontSize: "13px !important",
  },
  dialogBtnConfirm: {
    borderRadius: 8,
    fontWeight: 600,
    background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
    textTransform: "none",
    padding: "8px 20px",
    fontSize: "13px !important",
    boxShadow: "0 2px 8px rgba(220, 38, 38, 0.35)",
  },
}));

const ManageVehicle = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [makeSelected] = useContext(Context);

  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const RefreshDataSet = async () => {
    await dispatch(OAuthActions.getVehicleDetails(makeSelected.uid)).then(
      function (vehicleDetails) {
        if (!vehicleDetails || !vehicleDetails?._embedded?.vehicleModelDtoes) {
          setVehicles([]);
          return;
        }

        const newDataSet = vehicleDetails._embedded.vehicleModelDtoes.map(
          (item) => ({
            uid: item.uid || "",
            modelName: item.modelName || "",
            modelCode: item.modelCode || "",
            variant: item.variant || "",
            yearOfManufacturing: item.yearOfManufacturing || "",
            market: item.market || "",
            isInspectionJobAvailable: item.isInspectionJobAvailable !== false,
          })
        );

        setVehicles(newDataSet);
      }
    );
  };

  const loadVehicles = () => {
    RefreshDataSet();
  };

  useEffect(() => {
    RefreshDataSet();
  }, [makeSelected]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return vehicles.filter((v) => {
      return (
        (v.modelName || "").toLowerCase().includes(q) ||
        (v.modelCode || "").toLowerCase().includes(q) ||
        (v.variant || "").toLowerCase().includes(q) ||
        (v.yearOfManufacturing || "").toLowerCase().includes(q) ||
        (v.market || "").toLowerCase().includes(q)
      );
    });
  }, [search, vehicles]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleAddVehicle = () => {
    setAddDialogOpen(true);
  };

  const handleDeleteVehicle = (vehicleUid, modelName) => {
    setVehicleToDelete({ uid: vehicleUid, modelName });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!vehicleToDelete || isDeleting) return;
    setIsDeleting(true);
    dispatch(OAuthActions.deleteVehicleModel(vehicleToDelete.uid, makeSelected.uid))
      .then((response) => {
        if (response.status === 204) {
          setDeleteDialogOpen(false);
          setVehicleToDelete(null);
          dispatch(setSnackbar(true, "success", "Vehicle deleted successfully"));
          loadVehicles();
        } else {
          setDeleteDialogOpen(false);
          setVehicleToDelete(null);
          dispatch(setSnackbar(true, "error", "Failed to delete vehicle"));
        }
      })
      .catch((err) => {
        setDeleteDialogOpen(false);
        setVehicleToDelete(null);
        dispatch(setSnackbar(true, "error", "Failed to delete vehicle"));
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  return (
    <Box className={classes.root}>
      <Box component="main" className={classes.main}>
        <Box className={classes.pageHeader}>
          <Box className={classes.pageHeaderLeft}>
            <Box className={classes.pageIconBox}>
              <DirectionsCarIcon style={{ fontSize: 22, color: "white" }} />
            </Box>

            <Typography variant="h5">
              <strong style={{ fontSize: "1.4rem" }}>Manage Vehicle</strong>
            </Typography>
          </Box>

          <Box className={classes.headerActions}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVehicle}
              className={classes.addButton}
              style={{ fontSize: 20 }}
            >
              Add Vehicle
            </Button>
          </Box>
        </Box>

        <Paper variant="outlined" className={classes.tableCard}>
          <Box className={classes.toolbar}>
            <TextField
              size="small"
              variant="outlined"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search vehicles..."
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  {[
                    { label: "Model Name", width: "18%" },
                    { label: "Model Code", width: "18%" },
                    { label: "Year of Manufacturing", width: "18%" },
                    { label: "Variant", width: "18%" },
                    { label: "Market", width: "18%" },
                    { label: "Actions", width: "10%" },
                  ].map(({ label, width }) => (
                    <TableCell
                      key={label}
                      className={classes.tableHeadCell}
                      style={{ width }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((vehicle, index) => (
                  <TableRow key={index} className={classes.normalRow}>
                    <TableCell>
                      <Chip
                        label={vehicle.modelName}
                        size="small"
                        className={classes.makeChip}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" noWrap display="block">
                        {vehicle.modelCode}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" noWrap display="block">
                        {vehicle.yearOfManufacturing}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" noWrap display="block">
                        {vehicle.variant || "--"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" noWrap display="block">
                        {vehicle.market || "--"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={
                          vehicle.isInspectionJobAvailable
                            ? "Cannot delete - Inspection job is available"
                            : "Delete vehicle"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteVehicle(vehicle.uid, vehicle.modelName)
                            }
                            disabled={vehicle.isInspectionJobAvailable}
                            style={{
                              color: vehicle.isInspectionJobAvailable
                                ? COLORS.slate200
                                : COLORS.red500,
                            }}
                          >
                            <DeleteIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {paginated.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      className={classes.emptyCell}
                    >
                      No vehicles match your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.footer}>
            <Box className={classes.footerLeft}>
              <Typography variant="body2" className={classes.footerText}>
                Rows per page:
              </Typography>

              <Select
                style={{ borderRadius: 8 }}
                size="small"
                variant="outlined"
                value={rowsPerPage}
                onChange={handleRowsPerPage}
                className={classes.rowsSelect}
              >
                {ROWS_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box className={classes.footerRight}>
              <Typography variant="body2" className={classes.pageText}>
                Page {page} of {totalPages}
              </Typography>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                shape="circular"
                size="small"
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      <AddVehicleDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={loadVehicles}
      />

      <Dialog
        open={deleteDialogOpen}
        keepMounted
        maxWidth="sm"
        fullWidth
        onClose={handleCancelDelete}
        classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
      >
        <Box className={classes.dialogHeader}>
          <Box className={classes.dialogHeaderIconBox}>
            <HighlightOffOutlinedIcon className={classes.dialogHeaderIcon} />
          </Box>
          <Typography className={classes.dialogHeaderTitle}>
            Confirm Delete Vehicle
          </Typography>
        </Box>

        <DialogContent className={classes.dialogBody}>
          <Typography className={classes.dialogContentMain}>
            Are you sure you want to delete the vehicle{" "}
            <strong>{vehicleToDelete?.modelName}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions className={classes.dialogFooter}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            className={classes.dialogBtnOutlined}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            className={classes.dialogBtnConfirm}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageVehicle;
